import { Input, InputRef } from "antd";
import { useEffect, useRef, useState } from "react";
import { useExistByNickName } from "../../../../community/ui/nickNameBottomSheet/model";
import { useCreateOrUpdateNickNameWeb, useGetNickNameDetailWeb } from "../../model";
import { useQueryClient } from "@tanstack/react-query";
import Lottie from "lottie-react";
import { CircleCloseIcon } from "../../../../../shared/icon";
import LoadingBig from '../../../../././../assets/images/lottie/loading_big.json';

interface IProps {
    nickName: string
    setNickName: Function
    propsRefetch?: Function
    setOpen?: Function
    propsInputRef?: React.RefObject<InputRef>
    isOnlyInput?: boolean
    isShowSaveBtn?: boolean
    className?: string
    classNames?: { saveBtn?: string }
    saveBtntext?: string
    getNickNameDetailWebEnabled?: boolean
    setPropsNickNameValid?: Function
    customPlaceholder?: string
    style?: React.CSSProperties

}

const NickNameCreateAndUpdateInput = ({ nickName, setNickName, propsRefetch, setOpen, propsInputRef, isOnlyInput = false, isShowSaveBtn, className, classNames, saveBtntext = '저장하기', getNickNameDetailWebEnabled, setPropsNickNameValid, customPlaceholder = '3글자 이상 입력', style }: IProps) => {
    const queryClient = useQueryClient();
    const [showCheckContent, setShowCheckContent] = useState<React.ReactNode>(<></>);
    const [isInputStop, setIsInputStop] = useState<boolean>(false)
    const inputStopTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [isNickNameValid, setIsNickNameValid] = useState<boolean>(false);
    const { data, refetch: detailRefetch } = useGetNickNameDetailWeb(getNickNameDetailWebEnabled);
    const [isInputLoading, setIsInputLoading] = useState<boolean>(false)
    const createOrUpdateNickNameWeb = useCreateOrUpdateNickNameWeb()
    const getValidationMessage = (inputValue: string): JSX.Element | null => {
        if (inputValue.length === 0) return <></>;
        if (/\s/.test(inputValue)) return <div className="body/b1 error/e700">닉네임은 띄어쓰기가 불가합니다.</div>;
        if (/[^a-zA-Z0-9가-힣]/.test(inputValue)) return <div className="body/b1 error/e700">특수문자 및 기호가 포함된 닉네임은 불가합니다.</div>;
        if (inputValue.length < 3) return <div className="body/b1 error/e700">3글자 이상 입력해주세요.</div>;
        return null;
    };
    const { data: isExistNickName, isFetching, refetch } = useExistByNickName(nickName, getValidationMessage(nickName) === null && isNickNameValid);

    const onClickNickNameCreateButton = () => {
        if (!isNickNameValid || createOrUpdateNickNameWeb.isPending) return;
        const createOrUpdateNickNameWebRequest: ICreateOrUpdateNickNameWebRequest = {
            id: data?.id,
            nickName: nickName
        }

        createOrUpdateNickNameWeb.mutate(createOrUpdateNickNameWebRequest,
            {
                onSuccess: (res) => {
                    if (res.data) {
                        propsRefetch?.()
                        detailRefetch()
                        setOpen?.(false);
                        queryClient.invalidateQueries({ queryKey: ['nickName'] })
                    }
                }
            })
    }

    const isInputStopTime = () => {
        if (inputStopTimerRef.current) clearTimeout(inputStopTimerRef.current);
        setIsInputStop(false)
        inputStopTimerRef.current = setTimeout(() => {
            setIsInputStop(true)
        }, 300)
    }

    useEffect(() => {
        if (!isInputStop) return;
        setIsInputLoading(false);
        getValidationMessage(nickName) === null && refetch();
    }, [isInputStop])

    useEffect(() => {
        const validationMessage = getValidationMessage(nickName);

        if (validationMessage) {
            setIsNickNameValid(false);
            setPropsNickNameValid?.(false)
            setShowCheckContent(validationMessage);
        } else {
            setShowCheckContent(<div style={{ height: '18px' }}></div>);
        }
    }, [nickName]);

    useEffect(() => {
        if (isFetching) return;
        setIsInputLoading(false)

        const validationMessage = getValidationMessage(nickName);
        if (validationMessage) return;
        if (isExistNickName === undefined) {
            setIsNickNameValid(false);
            setPropsNickNameValid?.(false)
            setShowCheckContent(<div className="body/b1 error/e700">네트워크에 문제가 있습니다.</div>);
            return
        }
        const isAvailable = !isExistNickName;
        const resultContent = isAvailable
            ? <div className="body/b1 success/sc600">사용 가능한 닉네임 입니다.</div>
            : <div className="body/b1 error/e700">이미 사용중인 닉네임입니다.</div>;

        setIsNickNameValid(isAvailable);
        setPropsNickNameValid?.(isAvailable);
        setShowCheckContent(resultContent);
    }, [isFetching]);

    return <>
        <Input
            ref={propsInputRef}
            allowClear={!(isInputLoading || isFetching) && { clearIcon: <CircleCloseIcon fill1="var(--basewhite)" fill2="var(--secondarys400)" stroke1="var(--secondarys400)" onClick={() => { setIsNickNameValid(false); setNickName('') }} /> }}
            className={"custom-input-textarea custom-nickname-input " + (className ?? '')}
            classNames={{ input: 'body/b2 base/black-important' }}
            value={nickName}
            maxLength={15}
            onChange={(e) => { setIsInputLoading(true); isInputStopTime(); setNickName(e.target.value ?? '') }}
            placeholder={customPlaceholder}
            suffix={(isInputLoading || isFetching) && <Lottie style={{ width: 'var(--space16)', height: 'var(--space16)' }} animationData={LoadingBig} autoplay={true} />}
            onPressEnter={onClickNickNameCreateButton}
            style={{ marginBottom: '8px', ...style }}
        />
        {showCheckContent}

        {!isOnlyInput && (isShowSaveBtn || isNickNameValid) &&
            <div className={"sub-title/st3 " + (classNames?.saveBtn ?? '' + ' ') + (!isNickNameValid || createOrUpdateNickNameWeb.isPending ? 'secondary/s400' : "base/white")}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space16) var(--space20)', marginTop: 'calc(100dvh - 190px - 51px - 39px)', borderRadius: 'var(--radius4)', backgroundColor: !isNickNameValid || createOrUpdateNickNameWeb.isPending ? 'var(--secondarys200)' : 'var(--baseblack)' }}
                onClick={() => onClickNickNameCreateButton()}>
                {saveBtntext}
                {createOrUpdateNickNameWeb.isPending && <Lottie style={{ width: 16, height: 16, paddingBlock: '1.5px', marginLeft: '8px' }} animationData={LoadingBig} autoplay={true} />}
            </div>}
    </>
}

export default NickNameCreateAndUpdateInput